import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Button,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import DownloadIcon from '@mui/icons-material/Download'
import designSpecs from '../../theme/designSpecs'
import { downloadImage } from '../../utils/downloadImage'

const ImagePreviewModal = ({ open, onClose, images, selectedIndex, onNavigate }) => {
  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, selectedIndex])

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      onNavigate(selectedIndex - 1)
    }
  }

  const handleNext = () => {
    if (selectedIndex < images.length - 1) {
      onNavigate(selectedIndex + 1)
    }
  }

  const handleDownload = async () => {
    await downloadImage(images[selectedIndex], `mocksy-generated-${selectedIndex + 1}.png`)
  }

  if (!images || images.length === 0) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: designSpecs.borderRadius['2xl'],
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 1)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
        }}
      >
        {/* Image Container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          {/* Previous Button */}
          {selectedIndex > 0 && (
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          {/* Image */}
          <Box
            component="img"
            src={images[selectedIndex]}
            alt={`Generated image ${selectedIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: 'calc(90vh - 150px)',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: designSpecs.borderRadius.lg,
            }}
          />

          {/* Next Button */}
          {selectedIndex < images.length - 1 && (
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>

        {/* Bottom Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            p: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            width: '100%',
          }}
        >
          {/* Image Counter */}
          <Box
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {selectedIndex + 1} / {images.length}
          </Box>

          {/* Download Button */}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              background: designSpecs.gradients.primary,
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            Download
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ImagePreviewModal
